import React, { useEffect, useMemo, useRef } from "react";
import { sum } from "lodash";
import { useParams } from "react-router-dom";

import { Edit } from "@mui/icons-material";
import {
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { UploadSkeleton } from "./UploadSkeleton";
import {
  ActionButtons,
  AudienceSegmentsSize,
  AudienceSize,
  FileUpload,
  HelpBox,
  HelpText,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { AudienceMiniStats, CustomerMode, GeoMode, SelectableMenuCard, TravelTimeMode } from "@/components/molecules";
import { useAudienceContext } from "@/context";
import { useAudience, useAudienceDialogs, useAudiencePageState, useAudienceWorkflow, useClient } from "@/hooks";
import { ActionType, AudienceSummary } from "@/types";
import {
  AudiencePageActionId,
  AudiencePageRules,
  audiencePageActions,
  createUploadDefinition,
  uploadModeOptions,
} from "@/utils";

const BUILD_ACTIONS: ActionType[] = ['build'];
const ACTION_ERRORS: AudiencePageRules["actionErrors"] = {
  BuildError: ['build'],
  InsightError: ['insight'],
  TraveltimeError: ['traveltime'],
};
const PAGE_ACTIONS: AudiencePageActionId[] = ['delete', 'share', 'unshare', 'clone', 'build', 'insight', 'export'];

const UploadPage: React.FC = () => {

  const { state, dispatch } = useAudienceContext();
  const client = useClient();

  // Fetch Page Data
  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience, loading: audLoad, error: audErr,
  } = useAudience(audienceId, "upload");
  const uploadFile = audience?.upload_file;
  const uploadMode = audience?.upload_mode;

  const workflow = useAudienceWorkflow(audienceId, audience);
  const { action, isRunning } = workflow;
  const dialogs = useAudienceDialogs(audience);
  const isMounted = useRef(false);

  // Upload status
  const hasFile = audience?.upload_file instanceof File;
  const readyToBuild = audience?.upload_mode !== undefined && hasFile && !!state.validFile?.format;
  const rules = useMemo(() => ({
    readyToBuild,
    lockWhileBusy: true,
    buildActions: BUILD_ACTIONS,
    actionErrors: ACTION_ERRORS,
  }), [readyToBuild]);

  const pageState = useAudiencePageState({
    workflow,
    audience,
    isNew: audienceId === undefined,
    loading: audLoad,
    loadError: !!audErr,
    audienceError: audErr,
    definition: createUploadDefinition(audience),
    rules,
  });

  const handleChangeMode = (mode: string) => {
    dispatch({ type: "CHANGE_UPLOAD_MODE", payload: mode });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files) {
      dispatch({ type: "ADD_UPLOAD_FILE", payload: files[0] });
    }
  };

  useEffect(() => {
    // Check the file that has been uploaded
    uploadMode
    && uploadFile
    && uploadFile instanceof File
    && workflow.validateFile(uploadFile, uploadMode);
  }, [uploadFile]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return;
    }
    dispatch({ type: "CLEAR_UPLOAD_FILE" });
    dispatch({ type: "CHECK_UPLOAD_FILE", payload: null });
  }, [uploadMode])

  const summariseAudience = (summary: AudienceSummary[]) => {
    return {
      num_postcodes: sum(summary.map((x) => x.num_postcodes)),
      num_sectors: sum(summary.map((x) => x.num_sectors)),
      num_households: sum(summary.map((x) => x.num_households)),
      population_count: sum(summary.map((x) => x.population_count)),
    };
  };

  const pageActions = useMemo(
    () => audiencePageActions(pageState, workflow, dialogs, PAGE_ACTIONS),
    [pageState, workflow, dialogs]
  );

  return (
    <PageContainer
      actions={<ActionButtons actions={pageActions} />}
      errors={pageState.errors}
      loading={isRunning()}
      loadingText={action.message}
    >
      {pageState.isLoading ? (
        <UploadSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ pb: 4 }}>
          {audience && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                {/* TODO - Move to component */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !pageState.isLocked && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={dialogs.promptRename}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats
                    audienceSize={summariseAudience(audience.audience_size)}
                  />
                )}
              </Grid>

              {/* Heading and Mode Select */}

              <Grid size={12}>
                <HelpText
                  text="Select Mode"
                  helpText="Define the type of upload audience you are creating"
                  variant="body1"
                />
              </Grid>
              <Grid container size={12} spacing={2}>
                {uploadModeOptions.map((option, index) => (
                  <Grid key={index} pb={2}>
                    <SelectableMenuCard
                      title={option.label}
                      text={option.description}
                      onClick={() => handleChangeMode(option.value)}
                      icon={option.icon}
                      disabled={option.disabled || pageState.isLocked}
                      flag={option.flag}
                      flagDesc={option.flagDescription}
                      selected={audience.upload_mode === option.value}
                      height="152px"
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Mode Options */}
              {audience.upload_mode && (
                <>
                  {audience.upload_mode === 'travel_time' ? (
                      <TravelTimeMode audience={audience} readOnly={pageState.isLocked} />
                    )
                  : audience.upload_mode === 'customer' ? (
                      <CustomerMode readOnly={pageState.isLocked} />
                      ) : (
                      <GeoMode readOnly={pageState.isLocked} />
                    )
                  }
                  {pageState.hasChanged && !hasFile && uploadFile && (
                    <Typography variant="caption" color="error">
                      Please re-upload your file if you would like to re-build your Audience,
                      the filename is listed below for convenience.
                    </Typography>
                  )}
                  <Grid size={12}>
                    <FileUpload
                      currentFile={audience.upload_file}
                      validFile={state.validFile}
                      loading={isRunning('check')}
                      onChange={handleFileUpload}
                      allowSegments={true}
                      disabled={pageState.isLocked}
                    />
                  </Grid>
                </>
              )}

              {/* Audience Size */}
              {audience.audience_size && (
                <>
                  <Grid size={12} mt={2}>
                    <HelpText
                      text="Audience Size"
                      helpText="The number of postcodes in your upload, and how many households/people this represents"
                      variant="body1"
                    />
                  </Grid>
                  {audience.audience_size.length > 1 ? (
                    <AudienceSegmentsSize data={audience.audience_size} />
                  ) : (
                    <AudienceSize data={audience.audience_size[0]} />
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default UploadPage;
